import React, { Component } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Slide,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    FormHelperText
} from "@mui/material";
import InputTextField from "../Reusables/InputTextField";
import UserAndPermissionService from "../../Services/UserAndPermissionService";
import { TransitionProps } from '@mui/material/transitions';
import states from "../Reusables/Utility"
import { ShowAlert } from "../ShowAlert";

interface NewUserFormProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: {
        user_id: string;
        email: string;
        message: string;
        status: string
    }) => void;
    accessToken: string;
}

interface Role {
    role_id: string;
    role_name: string;
}

interface Role {
    role_id: string;
    role_name: string;
}

interface NewUserFormState {
    firstName: string;
    lastName: string;
    email: string;
    addressLine1: string;
    addressLine2: string;
    country: string;
    city: string;
    state: string;
    zip: string;
    firstNameError: string;
    lastNameError: string;
    emailError: string;
    addressLine1Error: string;
    addressLine2Error: string;
    countryError: string;
    cityError: string;
    stateError: string;
    zipError: string;
    selectedRoleError: string;
    loading: boolean;
    error: string | null;
    roleList: Role[];
    selectedRoleName: string;
    selectedRole: Role | null;
    selectedRoleID: string
}

// Transition props
const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Main class for new user form....
class NewUserForm extends Component<NewUserFormProps, NewUserFormState> {
    constructor(props: NewUserFormProps) {
        super(props);
        this.state = {
            firstName: "",
            lastName: "",
            email: "",
            addressLine1: "",
            addressLine2: "",
            country: "",
            city: "",
            state: "",
            zip: "",
            firstNameError: "",
            lastNameError: "",
            emailError: "",
            addressLine1Error: "",
            addressLine2Error: "",
            countryError: "",
            cityError: "",
            stateError: "",
            zipError: "",
            selectedRoleError: "",
            loading: false,
            error: null,
            roleList: [],
            selectedRoleName: "",
            selectedRole: null,
            selectedRoleID: ""
        };
    }

    async componentDidMount() {
        this.getUserRoles();
    }

    private async getUserRoles() {
        try {
            const response = await UserAndPermissionService.getUserRoles(this.props.accessToken);

            if (!response.ok) {
                throw new Error("Failed to fetch user roles");
            }
            const data = await response.json();
            this.setState({
                roleList: data.roleList

            });
        } catch (error) {
            ShowAlert("Error", "Failed to fetch user Roles. Please try again later.", "error");
            console.error("Error fetching user roles:", error);
        }
    }

    // Field input change..
    handleChange = (field: keyof NewUserFormState, value: string) => {
        this.setState((prevState) => ({
            ...prevState,
            [field]: value,
            [`${field}Error`]: value.trim() ? "" : `${field.replace(/([A-Z])/g, ' $1')} is required`,
        }));
    };

    //For input validation
    validateFields = (): boolean => {
        const { firstName, lastName, email, addressLine1, country, city, state, zip, selectedRole } = this.state;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        //const zipRegex = /^\d{5}(-\d{4})?$/;
        const zipRegex = /^\d{5}$/;

        const firstNameError = firstName.trim() ? "" : "First name is required";
        const lastNameError = lastName.trim() ? "" : "Last name is required";
        const emailError = email.trim() && emailRegex.test(email) ? "" : "Enter a valid email address";
        const addressLine1Error = addressLine1.trim() ? "" : "Address Line 1 is required";
        const addressLine2Error = addressLine1.trim() ? "" : "Address Line 2 is required";
        //const countryError = country.trim() ? "" : "Country is required";
        const cityError = city.trim() ? "" : "City is required";
        const stateError = state.trim() ? "" : "State is required";
        const zipError = zip.trim() && zipRegex.test(zip) ? "" : "Enter a valid ZIP code(eg.12345)";
        const selectedRoleError = selectedRole !== null ? "" : "Select a valid role";

        this.setState({
            firstNameError,
            lastNameError,
            emailError,
            addressLine1Error,
            addressLine2Error,
            //countryError,
            cityError,
            stateError,
            zipError,
            selectedRoleError
        });

        return !(firstNameError || lastNameError || emailError || addressLine1Error || addressLine2Error || cityError || stateError || zipError || selectedRoleError);
    };

    // Handle on save..
    handleSubmit = async () => {
        if (!this.validateFields()) return;

        this.setState({ loading: true, error: null });

        try {
            let postData = {
                roleId: this.state.selectedRoleID,
                firstName: this.state.firstName,
                lastName: this.state.lastName,
                email: this.state.email,
                addressLine1: this.state.addressLine1,
                addressLine2: this.state.addressLine2,
                //country: this.state.country,
                city: this.state.city,
                state: this.state.state,
                zip: this.state.zip,
            };

            const response = await UserAndPermissionService.createUser(this.props.accessToken, postData);
            if (response.ok) {
                const responseData = await response.json();
                if (responseData.status) {
                    ShowAlert(
                        "PassportPay",
                        `User created successfully, ${this.state.firstName} ${this.state.lastName}.`,
                        "success"
                    );
                    this.props.onSubmit(responseData);
                } else {
                    ShowAlert("PassportPay", "This email is already in use. Please enter a different email.", "error");
                    return
                }
            } else {
                this.setState({ error: "Failed to add user. Please try again." });
            }
        } catch (error) {
            this.setState({ error: "Failed to add user. Please try again." });
        } finally {
            this.setState({ loading: false });
        }
    };

    //Role selection dropdown change...
    handleRoleSelection = async (e: any) => {
        const selectedRole = this.state.roleList.find(role => role.role_name === e.target.value);
        if (!selectedRole) return; // Safety check

        this.setState({
            selectedRole: selectedRole,
            selectedRoleName: selectedRole.role_name,
            selectedRoleID: selectedRole.role_id

        }, () => this.validateFields());
    };

    render() {
        const { open, onClose } = this.props;
        const {
            firstName,
            lastName,
            email,
            addressLine1,
            addressLine2,
            country,
            city,
            state,
            zip,
            firstNameError,
            lastNameError,
            emailError,
            addressLine1Error,
            addressLine2Error,
            countryError,
            cityError,
            stateError,
            zipError,
            selectedRoleError,
            loading,
            error,
        } = this.state;

        return (
            <Dialog open={open}
                onClose={onClose}
                fullWidth
                maxWidth="sm"
                TransitionComponent={Transition}
                keepMounted>
                <DialogTitle>Add New User</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={1}>
                        <InputTextField
                            //label="First Name"
                            value={firstName}
                            inputType="text"
                            placeholder="First Name"
                            error={!!firstNameError}
                            helperText={firstNameError}
                            onChange={(value: string) => this.handleChange("firstName", value)}
                        />
                        <InputTextField
                            //label="Last Name"
                            value={lastName}
                            inputType="text"
                            placeholder="Last Name"
                            error={!!lastNameError}
                            helperText={lastNameError}
                            onChange={(value: string) => this.handleChange("lastName", value)}
                        />
                        <InputTextField
                            //label="Email"
                            value={email}
                            inputType="email"
                            placeholder="Email"
                            error={!!emailError}
                            helperText={emailError}
                            onChange={(value: string) => this.handleChange("email", value)}
                        />
                        <InputTextField
                            //label="Address Line 1"
                            value={addressLine1}
                            inputType="text"
                            placeholder="Address Line 1"
                            error={!!addressLine1Error}
                            helperText={addressLine1Error}
                            onChange={(value: string) => this.handleChange("addressLine1", value)}
                        />
                        <InputTextField
                            //label="Address Line 2"
                            value={addressLine2}
                            inputType="text"
                            placeholder="Address Line 2"
                            error={!!addressLine2Error}
                            helperText={addressLine2Error}
                            onChange={(value: string) => this.handleChange("addressLine2", value)}
                        />
                        {/* <InputTextField
                            //label="Country"
                            value={country}
                            inputType="text"
                            placeholder="Country"
                            error={!!countryError}
                            helperText={countryError}
                            onChange={(value: string) => this.handleChange("country", value)}
                        /> */}
                        <InputTextField
                            //label="City"
                            value={city}
                            inputType="text"
                            placeholder="City"
                            error={!!cityError}
                            helperText={cityError}
                            onChange={(value: string) => this.handleChange("city", value)}
                        />
                        <FormControl fullWidth error={!!stateError} sx={{ marginTop: "15px" }}>
                            <InputLabel>State</InputLabel>
                            <Select
                                value={state}
                                onChange={(e) => this.handleChange("state", e.target.value as string)}
                                label="State"
                            >
                                {states.map((state) => (
                                    <MenuItem key={state.code} value={state.code}>
                                        {state.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {stateError && <FormHelperText>{stateError}</FormHelperText>}
                        </FormControl>
                        <InputTextField
                            //label="Zip"
                            value={zip}
                            inputType="text"
                            placeholder="Zip"
                            error={!!zipError}
                            helperText={zipError}
                            onChange={(value: string) => this.handleChange("zip", value)}
                        />
                        <FormControl sx={{ marginTop: "15px" }} error={!!selectedRoleError}>
                            <Select
                                value={this.state.selectedRoleName || ""}
                                displayEmpty
                                renderValue={(selected) => selected || "Select a role"}
                                onChange={this.handleRoleSelection}
                            >
                                {this.state.roleList.map((role) => (
                                    <MenuItem key={role.role_id} value={role.role_name}>
                                        {role.role_name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {selectedRoleError && <FormHelperText>{selectedRoleError}</FormHelperText>}
                        </FormControl>
                        {error && <p style={{ color: "red" }}>{error}</p>}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={this.handleSubmit}
                        color="secondary"
                        variant="contained"
                        disabled={loading}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default NewUserForm;